import React, { useState, useEffect, useCallback, Suspense } from 'react';
import { lazyWithReload } from '../utils/lazyWithReload';
import GlobalSidebar from './GlobalSidebar';
import MobileMenuButton from './MobileMenuButton';
import MigrationBanner from './MigrationBanner';
import { FEATURES } from '../config/features';
import { BRAND_GREEN } from '../theme/vukzTheme';
import { useRBAC } from '../contexts/RBACContext';
import { AssistProvider, useAssist } from '../contexts/AssistContext';
import { useAssistFormBridge } from '../contexts/AssistFormBridge';
import EmbeddedAssistShell from './assist/EmbeddedAssistShell';
import CanvasHost from './assist/CanvasHost';

const CommandPaletteOverlay = lazyWithReload(() => import('./assist/CommandPaletteOverlay'));

// C4d-1 (Workspace Shell): a área central recua quando o Assist está aberto
// em desktop (coluna, não overlay). Consome useAssist, então precisa viver
// DENTRO do AssistProvider — por isso é um componente interno ao Layout.
const WorkspaceArea = ({ children }) => {
  const { asideWidth } = useAssist();
  return (
    <div className={`min-h-screen bg-gray-50${FEATURES.ENABLE_CLAW_SIGNATURE ? ' sucron-claw-watermark' : ''}`}>
      {/* pt-14 on mobile (56px mobile bar), pt-0 on desktop (no header).
          CANVAS-LAYOUT-2: a margem direita vem de UMA fonte — asideWidth do
          AssistContext (0 recolhido | 400 chat), também exposta como
          --vukz-aside-w. Em <lg o chat é drawer e não recua o workspace. */}
      <div
        data-testid="workspace-area"
        className="pt-14 lg:pt-0 lg:ml-72 transition-[margin] duration-200"
        style={{ marginRight: asideWidth ? `${asideWidth}px` : 0 }}
      >
        {children}
      </div>
    </div>
  );
};

// CANVAS-LAYOUT-2 (02/10/2026): o centro alterna entre a ROTA e o CANVAS.
//   canvas.isOpen  → mostra CanvasHost, esconde a rota (CSS `hidden`, NÃO
//                    desmonta: scroll/filtros/estado da página sobrevivem)
//   !canvas.isOpen → mostra a rota
// Exceção: página com formulário registrado na ponte (ex.: Training Builder,
// `training_builder`) é ela mesma a área de trabalho → a rota vence e o
// Canvas fica montado por trás. É o que faz "Revisar no Builder" trocar o
// centro Canvas → Builder com o chat fixo à direita, e o Canvas (rev N+1)
// reaparecer ao sair do Builder.
const MainWorkspace = ({ children }) => {
  const { canvas } = useAssist();
  const { activeFormId } = useAssistFormBridge();
  const canvasOnStage = !!(canvas.isOpen && canvas.kind && !activeFormId);
  return (
    <div data-testid="main-workspace" data-stage={canvasOnStage ? 'canvas' : 'route'}>
      <div data-testid="route-content" hidden={canvasOnStage}>
        {children}
      </div>
      <div
        data-testid="canvas-stage"
        hidden={!canvasOnStage}
        className="h-[calc(100vh-3.5rem)] lg:h-screen bg-white lg:border-l lg:border-gray-100"
      >
        <CanvasHost />
      </div>
    </div>
  );
};

const ROLE_LABELS = {
  ATLETA: 'Atleta',
  TREINADOR: 'Treinador',
  PREPARADOR_FISICO: 'Prep. Físico',
  GESTOR: 'Gestor',
  ADMIN: 'Admin'
};

const Layout = ({ children, quickStats = { today: 12, live: 3, championships: 20 } }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const { activeRole } = useRBAC();

  // Ctrl+K / Cmd+K opens command palette
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setPaletteOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const closePalette = useCallback(() => setPaletteOpen(false), []);

  return (
    <AssistProvider>
      <MigrationBanner />
      <GlobalSidebar 
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        onClose={() => setSidebarOpen(false)}
        quickStats={quickStats}
      />
      
      {/* Mobile top bar — logo + hamburger (hidden on desktop, sidebar is visible) */}
      <header className="fixed top-0 left-0 right-0 h-14 bg-white border-b border-gray-200 flex items-center justify-between px-4 z-30 lg:hidden">
        <div className="flex items-center gap-3">
          <img 
            src="/vukz-logo.jpeg" 
            alt="VUKZ" 
            style={{ height: '40px' }}
            className="w-auto object-contain"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          {activeRole && (
            <span 
              className="text-xs font-bold px-2 py-1 rounded-lg"
              style={{ backgroundColor: '#f0fdf4', color: BRAND_GREEN }}
            >
              {ROLE_LABELS[activeRole] || activeRole}
            </span>
          )}
        </div>
        <MobileMenuButton isOpen={mobileMenuOpen} onToggle={() => setMobileMenuOpen(prev => !prev)} onClose={() => setMobileMenuOpen(false)} />
      </header>
      
      <WorkspaceArea>
        <MainWorkspace>{children}</MainWorkspace>
      </WorkspaceArea>

      {/* Command Palette Overlay (Ctrl+K) */}
      <Suspense fallback={null}>
        {paletteOpen && (
          <CommandPaletteOverlay isOpen={paletteOpen} onClose={closePalette} />
        )}
      </Suspense>

      {/* Global AI Assistant (VUKZ Assist) */}
      <EmbeddedAssistShell />
    </AssistProvider>
  );
};

export default Layout;